import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, CalendarDays, Video, Phone, Building2, Monitor, ExternalLink, RotateCcw, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/admin/StatusBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const MODE_ICONS = { google_meet: Video, zoom: Video, teams: Monitor, phone: Phone, office: Building2 };
const MODE_LABELS = { google_meet: "Google Meet", zoom: "Zoom", teams: "MS Teams", phone: "Phone Call", office: "Office Visit" };

export default function MyAppointments() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loadingApts, setLoadingApts] = useState(true);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [newTime, setNewTime] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoadingApts(true);
    try {
      const { data } = await api.get("/appointments/me");
      setAppointments(data.appointments || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoadingApts(false); }
  }, [user]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const cancel = async (id) => {
    try {
      await api.post(`/appointments/${id}/cancel`);
      toast.success("Appointment cancelled");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reschedule = async () => {
    if (!newTime || !rescheduleId) return;
    setSubmitting(true);
    try {
      await api.post(`/appointments/${rescheduleId}/reschedule`, { scheduled_at: new Date(newTime).toISOString() });
      toast.success("Reschedule request submitted");
      setRescheduleId(null);
      setNewTime("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--zt-beige))]">
        <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--zt-beige))] pb-24">
      {/* Header */}
      <div className="bg-[hsl(var(--zt-teal))] text-white relative overflow-hidden">
        <div aria-hidden className="absolute -top-40 -right-40 w-[450px] h-[450px] rounded-full opacity-[0.15] blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 60%)" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-24 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="transition-transform hover:scale-[1.02]">
              <img src="/zenstrust-logo.png" alt="Zenstrust" className="h-14 w-auto rounded-md object-contain" />
            </button>
            <span className="hidden sm:inline font-heading uppercase tracking-[0.25em] text-[10px] text-[hsl(var(--zt-gold))] font-bold border-l border-white/15 pl-4">
              My Appointments
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-5 rounded-full border border-white/10">
              Dashboard
            </Button>
            <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); }} className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-5 rounded-full border border-white/10">
              <LogOut size={15} className="mr-2" /> Log out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl lg:text-4xl text-[hsl(var(--zt-teal))] font-bold">Your Appointments</h1>
          <span className="font-heading uppercase tracking-[0.2em] text-[10px] text-[hsl(var(--zt-teal))]/60 font-bold bg-[hsl(var(--zt-teal))]/5 border border-[hsl(var(--zt-teal))]/10 px-3 py-1 rounded-full">
            {appointments.length} total
          </span>
        </div>

        {loadingApts ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} />
          </div>
        ) : appointments.length === 0 ? (
          <div className="bg-white border border-dashed border-[hsl(var(--zt-teal))]/20 rounded-3xl p-16 text-center">
            <CalendarDays size={32} className="mx-auto mb-4 text-[hsl(var(--zt-teal))]/30" />
            <p className="font-heading text-[hsl(var(--zt-teal))] text-lg font-bold">No appointments yet</p>
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/55 mt-2">Your scheduled advisory sessions will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-5">
            {appointments.map(a => {
              const ModeIcon = MODE_ICONS[a.meeting_mode] || Video;
              const isPast = new Date(a.scheduled_at) < new Date();
              const canAct = !isPast && !["cancelled", "completed"].includes(a.status);

              return (
                <div key={a.appointment_id}
                  className="bg-white border border-[hsl(var(--zt-beige-deep))] rounded-3xl p-6 lg:p-8 grid lg:grid-cols-12 gap-6 items-center shadow-sm hover:shadow-md transition-shadow">
                  {/* Date/Time block */}
                  <div className="lg:col-span-2">
                    <div className="bg-[hsl(var(--zt-teal))]/5 border border-[hsl(var(--zt-teal))]/10 rounded-2xl p-4 text-center">
                      <p className="font-heading uppercase tracking-[0.15em] text-[9px] text-[hsl(var(--zt-teal))]/60 font-bold">{fmtMonth(a.scheduled_at)}</p>
                      <p className="font-display text-3xl text-[hsl(var(--zt-teal))] font-bold leading-none my-1">{fmtDay(a.scheduled_at)}</p>
                      <p className="font-heading text-xs font-bold text-[hsl(var(--zt-charcoal))]/65">{fmtTime(a.scheduled_at)}</p>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="lg:col-span-6 space-y-2">
                    <p className="font-heading text-[hsl(var(--zt-teal))] font-bold text-base">{a.advisor_name || "Your Advisor"}</p>
                    <div className="flex items-center gap-2">
                      <ModeIcon size={14} className="text-[hsl(var(--zt-gold))]" />
                      <span className="text-sm text-[hsl(var(--zt-charcoal))]/65">{MODE_LABELS[a.meeting_mode]}</span>
                      <span className="text-[hsl(var(--zt-charcoal))]/30">·</span>
                      <span className="text-sm text-[hsl(var(--zt-charcoal))]/65">{a.duration_mins} min</span>
                    </div>
                    {a.notes && <p className="text-sm text-[hsl(var(--zt-charcoal))]/55 italic">"{a.notes}"</p>}
                  </div>

                  {/* Status + actions */}
                  <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                    <StatusBadge status={a.status} />

                    <div className="flex flex-wrap gap-2">
                      {a.meeting_link && canAct && (
                        <a href={a.meeting_link} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 bg-[hsl(var(--zt-gold))] text-white rounded-full px-4 h-9 font-heading uppercase tracking-wider text-xs font-bold hover:bg-[hsl(var(--zt-gold))]/90 transition-colors">
                          Join <ExternalLink size={11} />
                        </a>
                      )}

                      {canAct && (
                        <button onClick={() => setRescheduleId(a.appointment_id)}
                          className="inline-flex items-center gap-1.5 border border-[hsl(var(--zt-teal))]/20 text-[hsl(var(--zt-teal))]/70 rounded-full px-4 h-9 font-heading uppercase tracking-wider text-xs hover:border-[hsl(var(--zt-teal))]/40 hover:text-[hsl(var(--zt-teal))] transition-colors">
                          <RotateCcw size={11} /> Reschedule
                        </button>
                      )}

                      {canAct && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="inline-flex items-center gap-1.5 border border-red-200 text-red-500 rounded-full px-4 h-9 font-heading uppercase tracking-wider text-xs hover:bg-red-50 transition-colors">
                              <X size={11} /> Cancel
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel appointment?</AlertDialogTitle>
                              <AlertDialogDescription>This cannot be undone. Your advisor will be notified.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep</AlertDialogCancel>
                              <AlertDialogAction onClick={() => cancel(a.appointment_id)} className="bg-red-500 hover:bg-red-600">Cancel Appointment</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reschedule dialog */}
      {rescheduleId && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl">
            <h3 className="font-display text-xl text-[hsl(var(--zt-teal))] font-bold mb-2">Reschedule</h3>
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/60 mb-5">Select a new date and time for your appointment.</p>
            <input
              type="datetime-local"
              value={newTime}
              onChange={e => setNewTime(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 mb-4"
            />
            <div className="flex gap-3">
              <Button onClick={reschedule} disabled={!newTime || submitting}
                className="flex-1 bg-[hsl(var(--zt-teal))] text-white rounded-full h-10 font-heading uppercase tracking-wider text-xs">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : "Confirm"}
              </Button>
              <Button variant="outline" onClick={() => setRescheduleId(null)} className="rounded-full h-10 px-5 font-heading uppercase tracking-wider text-xs">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtMonth(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}
function fmtDay(d) {
  if (!d) return "";
  return String(new Date(d).getDate());
}
function fmtTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
